import moment from 'moment';
import React from 'react';
import Avatar from '../../components/Avatar';
import SVGIcon from '../../components/SVGIcon';
import Tooltip from '../../components/Tooltip';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import { AuditLogType } from '../../graphql/generated/graphql';
import informationIcon from '../../icons/non-prefilled/16x16/information.svg';
import {
  INSTALLABLE_PLATFORM_INFO_MAP,
  SEARCH_PLATFORM_INFO_MAP,
} from '../../integrations/IntegrationPlatformInfo';
import { useAllUsersMap } from '../../user/useAllUsers';
import styles from './AuditLogRow.module.scss';

const PROVISIONS_FRAGMENT = gql(`
  fragment AuditLogRow_provisions on Provision {
    id
    name
  }
`);

type ProvisionFragment = DocumentType<typeof PROVISIONS_FRAGMENT>;

const AUDIT_LOG_FRAGMENT = gql(`
  fragment AuditLogRow_auditLog on AuditLog {
    id
    type
    insertedAt
    detail {
      approverId
      from
      groupId
      inviterId
      ip
      name
      platform
      postId
      privacy
      draft
      provisionId
      targetId
      team
      templateId
      title
      to
      topicId
    }
    user {
      id
      name
      ...Avatar_user
    }
  }
`);

type AuditLogFragment = DocumentType<typeof AUDIT_LOG_FRAGMENT>;

const getPlatformName = (platform?: string | null) => {
  if (!platform) return '';

  return (
    INSTALLABLE_PLATFORM_INFO_MAP[platform]?.name ??
    SEARCH_PLATFORM_INFO_MAP[platform]?.name ??
    platform
  );
};

const isNoPermissionCase = (auditLogRow: AuditLogFragment) =>
  auditLogRow.detail?.privacy === 'secret' ||
  auditLogRow.detail?.privacy === 'private' ||
  auditLogRow.detail?.draft;

const getAuditLogAction = (
  auditLogRow: AuditLogFragment,
  allUsersMap: Record<string, { name: string }>,
  provisions?: ProvisionFragment[],
) => {
  switch (auditLogRow.type) {
    case AuditLogType.LoginSuccess:
      return (
        <>
          <strong>logged in</strong> with password
        </>
      );
    case AuditLogType.LoginFailure:
      return (
        <>
          <strong>failed login</strong> with password
        </>
      );
    case AuditLogType.LoginSso:
      return (
        <>
          <strong>logged in</strong> with{' '}
          {getPlatformName(auditLogRow.detail?.platform)} SSO
        </>
      );
    case AuditLogType.LoginMagic:
      return (
        <>
          <strong>logged in</strong> with magic email link
        </>
      );
    case AuditLogType.ResetSuccess:
      return (
        <>
          <strong>reset login</strong> password
        </>
      );
    case AuditLogType.ResetRequest:
      return (
        <>
          <strong>requested</strong> a password reset
        </>
      );
    case AuditLogType.UserUpdate:
      return (
        <>
          <strong>updated</strong>{' '}
          {allUsersMap[auditLogRow.detail?.targetId ?? '']?.name} to{' '}
          {auditLogRow.detail?.to} from {auditLogRow.detail?.from}
        </>
      );
    case AuditLogType.UserDeactivate:
      return (
        <>
          <strong>deactivated</strong>{' '}
          {allUsersMap[auditLogRow.detail?.targetId ?? '']?.name}
        </>
      );
    case AuditLogType.UserReactivate:
      return (
        <>
          <strong>reactivated</strong>{' '}
          {allUsersMap[auditLogRow.detail?.targetId ?? '']?.name}
        </>
      );
    case AuditLogType.UserCreate:
      if (auditLogRow.detail?.inviterId) {
        return (
          <>
            <strong>joined</strong> with invitation from{' '}
            {allUsersMap[auditLogRow.detail.inviterId ?? '']?.name}
          </>
        );
      }
      if (auditLogRow.detail?.provisionId) {
        const provision = provisions?.find(
          (p) => p.id === auditLogRow.detail?.provisionId,
        );
        return (
          <>
            <strong>joined</strong> with {provision?.name} provision link
          </>
        );
      }
      if (auditLogRow.detail?.platform) {
        return (
          <>
            <strong>joined</strong> with{' '}
            {getPlatformName(auditLogRow.detail.platform)} SSO
          </>
        );
      }
      return null;
    case AuditLogType.IntegrationAdd:
      return (
        <>
          <strong>
            added {auditLogRow.detail?.team ? 'team' : 'individual'} integration
          </strong>{' '}
          {getPlatformName(auditLogRow.detail?.platform)}
        </>
      );
    case AuditLogType.IntegrationUpdate:
      return (
        <>
          <strong>
            updated {auditLogRow.detail?.team ? 'team' : 'individual'}{' '}
            integration
          </strong>{' '}
          {getPlatformName(auditLogRow.detail?.platform)}
        </>
      );
    case AuditLogType.IntegrationDelete:
      return (
        <>
          <strong>
            removed {auditLogRow.detail?.team ? 'team' : 'individual'}{' '}
            integration
          </strong>{' '}
          {getPlatformName(auditLogRow.detail?.platform)}
        </>
      );
    case AuditLogType.PostDelete:
      return isNoPermissionCase(auditLogRow) ? (
        <>
          <strong>deleted post</strong> {auditLogRow.detail?.postId}
        </>
      ) : (
        <>
          <strong>deleted post</strong> {auditLogRow.detail?.title}
        </>
      );
    case AuditLogType.TopicDelete:
      return isNoPermissionCase(auditLogRow) ? (
        <>
          <strong>deleted topic</strong> {auditLogRow.detail?.topicId}
        </>
      ) : (
        <>
          <strong>deleted topic</strong> {auditLogRow.detail?.name}
        </>
      );
    case AuditLogType.GroupDelete:
      return (
        <>
          <strong>deleted group</strong> {auditLogRow.detail?.name}
        </>
      );
    case AuditLogType.TemplateDelete:
      return isNoPermissionCase(auditLogRow) ? (
        <>
          <strong>deleted template</strong> {auditLogRow.detail?.templateId}
        </>
      ) : (
        <>
          <strong>deleted template</strong> {auditLogRow.detail?.title}
        </>
      );

    default:
      return null;
  }
};

const getTooltipText = (
  auditLogRow: AuditLogFragment,
  allUsersMap: Record<string, { name: string }>,
) => {
  switch (auditLogRow.type) {
    case AuditLogType.LoginSuccess:
    case AuditLogType.LoginFailure:
    case AuditLogType.LoginSso:
    case AuditLogType.LoginMagic:
    case AuditLogType.ResetSuccess:
      return auditLogRow.detail?.ip
        ? `IP Address: ${auditLogRow.detail?.ip}`
        : null;
    case AuditLogType.GroupDelete:
      return auditLogRow.detail?.groupId
        ? `Group ID: ${auditLogRow.detail?.groupId}`
        : null;
    case AuditLogType.PostDelete:
      if (auditLogRow.detail?.postId) {
        return isNoPermissionCase(auditLogRow)
          ? auditLogRow.detail.title
          : `Post ID: ${auditLogRow.detail?.postId}`;
      }
      return null;
    case AuditLogType.TemplateDelete:
      if (auditLogRow.detail?.templateId) {
        return isNoPermissionCase(auditLogRow)
          ? auditLogRow.detail.title
          : `Template ID: ${auditLogRow.detail?.templateId}`;
      }
      return null;
    case AuditLogType.TopicDelete:
      if (auditLogRow.detail?.topicId) {
        return isNoPermissionCase(auditLogRow)
          ? auditLogRow.detail.name
          : `Topic ID: ${auditLogRow.detail?.topicId}`;
      }
      return null;
    case AuditLogType.UserCreate:
      return auditLogRow.detail?.approverId
        ? // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
          `Approved by ${allUsersMap[auditLogRow.detail.approverId]?.name}`
        : null;
    default:
      return null;
  }
};

interface AuditLogRowProps {
  auditLog: AuditLogFragment;
  provisions?: ProvisionFragment[];
}

const AuditLogRow = ({ auditLog, provisions }: AuditLogRowProps) => {
  const allUsersMap = useAllUsersMap({
    includeDeactivated: true,
  });

  const action = getAuditLogAction(auditLog, allUsersMap, provisions);

  if (!action) {
    return null;
  }

  const tooltip = getTooltipText(auditLog, allUsersMap);

  return (
    <div className={styles.row}>
      <div className={styles.avatar}>
        <Avatar user={auditLog.user} size={24} />
      </div>
      <div className={styles.action}>
        {auditLog.user.name} {action}&nbsp;&nbsp;
        {tooltip && (
          <div className={styles.tooltip}>
            <Tooltip trigger="clickOrHover" content={tooltip} display="inline">
              <SVGIcon className={styles.icon} inline>
                {informationIcon}
              </SVGIcon>
            </Tooltip>
          </div>
        )}
      </div>
      <div className={styles.date}>
        {moment(auditLog.insertedAt).format('M/D')}
      </div>
      <div className={styles.time}>
        {moment(auditLog.insertedAt).format('h:mma')}
      </div>
    </div>
  );
};

export default AuditLogRow;
